import React, { SyntheticEvent, useState } from 'react';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import { Link as ReactRouterLink } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Dealer, DealerEmailTemplate } from '../../../models/dealer.model';
import config from '../../../config';

const HARD_CODED_DEALERS: Dealer[] = [
  {
    id: 'dealer-001',
    companyName: 'Futura Trailers Inc.',
    phone: '(512) 555-0101',
    emailAddresses: ['sales@futuratrailers.com'],
    emailTemplate: DealerEmailTemplate.FuturaTrailersInc,
    isArchived: false,
    address: {
      addressLine1: '1234 Trailer Blvd',
      city: 'Austin',
      state: 'TX',
      zipCode: '78701',
    },
    documents: [],
  },
  {
    id: 'dealer-002',
    companyName: 'Lone Star Haulers',
    phone: '(214) 555-0202',
    emailAddresses: ['info@lonstarhaulers.com'],
    emailTemplate: DealerEmailTemplate.Default,
    isArchived: false,
    address: {
      addressLine1: '567 Commerce Way',
      city: 'Dallas',
      state: 'TX',
      zipCode: '75201',
    },
    documents: [],
  },
  {
    id: 'dealer-003',
    companyName: 'Gulf Coast Cargo',
    phone: '(713) 555-0303',
    emailAddresses: ['hello@gulfcoastcargo.com'],
    emailTemplate: DealerEmailTemplate.Default,
    isArchived: false,
    address: {
      addressLine1: '890 Port Ave',
      city: 'Houston',
      state: 'TX',
      zipCode: '77001',
    },
    documents: [],
  },
  {
    id: 'dealer-004',
    companyName: 'Southwest Transport Co.',
    phone: '(602) 555-0404',
    emailAddresses: ['contact@swtransport.com'],
    emailTemplate: DealerEmailTemplate.Default,
    isArchived: true,
    address: {
      addressLine1: '321 Desert Road',
      city: 'Phoenix',
      state: 'AZ',
      zipCode: '85001',
    },
    documents: [],
  },
];

const copyDealerLink = async (dealerId: string, setOpenAlertCopy?: (v: boolean) => void) => {
  try {
    const dealerLink = `${config.app.web_url}affiliation-form/${dealerId}`;
    await navigator.clipboard.writeText(dealerLink);
    setOpenAlertCopy && setOpenAlertCopy(true);
  } catch (err) {
    console.error('Failed to copy: ', err);
  }
};

function DealerTableRow({
  row,
  handleShowArchiveModal,
  handleShowUnarchiveModal,
  onCopyLink,
}: {
  row: Dealer;
  handleShowArchiveModal: (id: string) => void;
  handleShowUnarchiveModal: (id: string) => void;
  onCopyLink: (id: string) => void;
}) {
  return (
    <TableRow selected={row.isArchived}>
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Tooltip title="Copy Dealer Link">
            <ContentCopyIcon
              color="primary"
              fontSize="small"
              onClick={() => onCopyLink(row.id)}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Send Dealer Link">
            <EmailIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'not-allowed' }} />
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell>{row.id}</TableCell>
      <TableCell>
        <ReactRouterLink to={`/dealers/${row.id}`}>
          <span>{row.companyName}</span>
        </ReactRouterLink>
      </TableCell>
      <TableCell>{row.address.addressLine1}</TableCell>
      <TableCell>{row.address.city}</TableCell>
      <TableCell>{row.address.state}</TableCell>
      <TableCell>{row.address.zipCode}</TableCell>
      <TableCell>{row.phone}</TableCell>
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {!row.isArchived && (
            <Tooltip title="Archive">
              <ReactRouterLink onClick={() => handleShowArchiveModal(row.id)} to="">
                <ArchiveIcon color="error" fontSize="small" />
              </ReactRouterLink>
            </Tooltip>
          )}
          {row.isArchived && (
            <Tooltip title="Unarchive">
              <ReactRouterLink onClick={() => handleShowUnarchiveModal(row.id)} to="">
                <UnarchiveIcon fontSize="small" />
              </ReactRouterLink>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}

function DealerTable({
  data,
  handleShowArchiveModal,
  handleShowUnarchiveModal,
  onCopyLink,
}: {
  data: Dealer[];
  handleShowArchiveModal: (id: string) => void;
  handleShowUnarchiveModal: (id: string) => void;
  onCopyLink: (id: string) => void;
}) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell> </TableCell>
            <TableCell>id</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Address</TableCell>
            <TableCell>City</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Zipcode</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell> </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <DealerTableRow
              key={row.id}
              row={row}
              handleShowArchiveModal={handleShowArchiveModal}
              handleShowUnarchiveModal={handleShowUnarchiveModal}
              onCopyLink={onCopyLink}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function DealersNewContainer() {
  const [dealers, setDealers] = useState<Dealer[]>(HARD_CODED_DEALERS);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [currentItemId, setCurrentItemId] = useState('');
  const [openAlertCopy, setOpenAlertCopy] = useState(false);

  const handleShowArchiveModal = (id: string) => {
    setCurrentItemId(id);
    setShowArchiveModal(true);
  };

  const handleShowUnarchiveModal = (id: string) => {
    setCurrentItemId(id);
    setShowUnarchiveModal(true);
  };

  const handleArchive = () => {
    setDealers((prev) => prev.map((d) => (d.id === currentItemId ? { ...d, isArchived: true } : d)));
    setShowArchiveModal(false);
  };

  const handleUnarchive = () => {
    setDealers((prev) => prev.map((d) => (d.id === currentItemId ? { ...d, isArchived: false } : d)));
    setShowUnarchiveModal(false);
  };

  const handleCloseAlertCopy = (_event: Event | SyntheticEvent<any, Event>, reason: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenAlertCopy(false);
  };

  return (
    <React.Fragment>
      <Grid size={{ xs: 12 }}>
        <div className="Dealers">
          <div className="dealers">
            <h2 className="pb-2 mt-4 mb-3 border-bottom">Dealers</h2>
          </div>
          <div className="pb-2 mt-4 mb-3">
            <ReactRouterLink to="/dealers-v2/new">
              <Button variant="contained">DEALERS</Button>
            </ReactRouterLink>
          </div>
          <DealerTable
            data={dealers}
            handleShowArchiveModal={handleShowArchiveModal}
            handleShowUnarchiveModal={handleShowUnarchiveModal}
            onCopyLink={(id) => copyDealerLink(id, setOpenAlertCopy)}
          />
        </div>
      </Grid>

      <Snackbar open={openAlertCopy} autoHideDuration={6000} onClose={handleCloseAlertCopy}>
        <Alert severity="success" sx={{ width: '100%' }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>

      <Dialog
        open={showArchiveModal}
        onClose={(_event, reason) => { if (reason === 'escapeKeyDown') return; setShowArchiveModal(false); }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Are you sure you want to archive this?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchive}>Archive</Button>
          <Button onClick={() => setShowArchiveModal(false)} autoFocus>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUnarchiveModal}
        onClose={(_event, reason) => { if (reason === 'escapeKeyDown') return; setShowUnarchiveModal(false); }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Are you sure you want to unarchive this?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnarchive}>Unarchive</Button>
          <Button onClick={() => setShowUnarchiveModal(false)} autoFocus>Cancel</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
